import type { useFormatter } from 'next-intl';
import { ONE_DAY } from '@/app/constants';

export const periodOfValidity = (period: [string?, string?] = [], x: Date = new Date()) => {
  const [startTime, endTime] = period;
  // 如果开始时间和结束时间都为空，返回 true
  if (!startTime && !endTime) {
    return true;
  }
  const time = new Date(x).getTime();
  // 检查 x 时刻是否在开始时间和结束时间之间
  const isAfterStart = startTime ? time >= new Date(startTime).getTime() : true;
  const isBeforeEnd = endTime ? time <= new Date(endTime).getTime() : true;
  return isAfterStart && isBeforeEnd;
};

/**
 * 将秒数转换为 "00:00" 格式
 */
export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * 将当前日期或者指定日期添加一定天数
 */
export const addDays = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

/**
 * 获取今天的日期字符串（格式：YYYY-MM-DD）
 * @param date 可选，指定日期，默认为当前日期
 * @returns 日期字符串，格式如 "2024-01-15"
 */
export const getTodayDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 计算两个日期之间相差的本地自然日天数
 */
export const getLocalDayDiff = (date: Date, baseDate: Date = new Date()): number => {
  const getLocalDayNumber = (value: Date) =>
    Math.floor(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / ONE_DAY);

  return getLocalDayNumber(baseDate) - getLocalDayNumber(date);
};

/**
 * 计算从当前本地时间到指定UTC时间的剩余天数
 * @param utcTimeString UTC时间字符串，格式如 "2025-07-30T00:00:00"
 * @returns 剩余天数，不足1天返回1，已过期返回0
 */
export const calculateDaysUntilUTC = (utcTimeString: string): number => {
  try {
    if (!utcTimeString) {
      return 0;
    }
    // 将UTC时间字符串转换为Date对象
    // 如果字符串不以Z结尾，手动添加Z确保按UTC解析
    const utcTime = new Date(utcTimeString.endsWith('Z') ? utcTimeString : `${utcTimeString}Z`);

    // 获取当前本地时间
    const currentTime = new Date();

    // 计算时间差（毫秒）
    const timeDiff = utcTime.getTime() - currentTime.getTime();

    // 如果已过期，返回0
    if (timeDiff <= 0) {
      return 0;
    }

    // 转换为天数
    const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    // 确保至少返回1天
    return Math.max(days, 1);
  } catch (error) {
    // 如果解析失败，返回0
    console.error('Failed to parse UTC time string:', utcTimeString, error);
    return 0;
  }
};

/**
 * 计算到指定UTC时间的剩余秒数
 * @param utcTimeString UTC时间字符串
 * @returns 剩余秒数，已过期返回0
 */
export const calculateSecondsUntilUTC = (utcTimeString: string): number => {
  try {
    if (!utcTimeString) {
      return 0;
    }
    const utcTime = new Date(utcTimeString.endsWith('Z') ? utcTimeString : `${utcTimeString}Z`);
    const currentTime = new Date();
    const timeDiff = utcTime.getTime() - currentTime.getTime();
    return timeDiff > 0 ? Math.floor(timeDiff / 1000) : 0;
  } catch (error) {
    console.error('Failed to parse UTC time string:', utcTimeString, error);
    return 0;
  }
};

/**
 * 将秒数转换为 "HH:MM:SS" 格式
 * @param seconds 秒数
 * @returns 格式化的时间字符串
 */
export const formatCountdownTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 格式化日期为 "YYYY.MM.DD"
 * @param date 日期
 * @returns 格式化后的日期，格式为 "YYYY.MM.DD"
 */
export const formatDate = (date: Date | string) => {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}.${month}.${day}`;
};

/**
 * 格式化日期时间显示（国际化）
 * @param date 日期
 * @param format next-intl 的 formatter 对象
 * @returns 格式化后的日期时间（使用用户本地时区显示）
 */
export const formatDateTime = (date: Date | string, format: ReturnType<typeof useFormatter>): string => {
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return '-';

  return format.dateTime(dateObj, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
};

/**
 * 检查时间戳是否超过指定间隔
 */
export const isTimestampExceeded = (timestamp: number | undefined, interval: number): boolean => {
  if (!timestamp) return true;
  return Date.now() - timestamp >= interval;
};

/**
 * 格式化到期时间显示
 * @param dateString - UTC时间字符串，如 "2026-02-21T00:00:00Z"
 * @param format - next-intl 的 formatter 对象
 * @returns 格式化后的日期字符串（使用用户本地时区显示）
 */
export const formatExpiredDate = (dateString: string | undefined, format: ReturnType<typeof useFormatter>): string => {
  if (!dateString) return '';
  const expiredDate = getExpiredDate(dateString);
  if (!expiredDate) return '';

  return format.dateTime(expiredDate, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
};

/**
 * 根据UTC时间确定到期日（基于本地时区）
 * 规则：
 * - 将UTC时间转换为本地时区
 * - 如果本地时区时间是某天的00:00:00，到期日应该是前一天
 * - 如果本地时区时间是某天的非00:00:00时间，到期日应该是当天
 * @param dateString - UTC时间字符串，如 "2026-02-21T00:00:00Z"
 * @returns 到期日期对象（本地时区）
 */
export const getExpiredDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const utcDate = new Date(dateString);

  // 获取本地时区的各个部分
  const year = utcDate.getFullYear();
  const month = utcDate.getMonth();
  const day = utcDate.getDate();
  const hour = utcDate.getHours();
  const minute = utcDate.getMinutes();
  const second = utcDate.getSeconds();

  let expiredDate: Date;
  if (hour === 0 && minute === 0 && second === 0) {
    // 如果本地时区时间是00:00:00，到期日应该是前一天
    expiredDate = new Date(year, month, day);
    expiredDate.setDate(expiredDate.getDate() - 1);
  } else {
    // 当天
    expiredDate = new Date(year, month, day);
  }

  return expiredDate;
};
